import React, { useEffect } from 'react'
import { Button, Card, Col, Layout, message, Row, Typography } from 'antd'
import { Trans, useTranslation } from 'react-i18next'
import { gutter } from '../../lib/layout'
import styles from '../../less/BaseLayout.module.less'
import { useLogo } from '../../lib/themeController'
import _get from 'lodash/get'

import { reload, signOut } from 'firebase/auth'
import {
  useAuth,
  useFirestore,
  useFirestoreDocData,
  useFunctions,
} from 'reactfire'
import * as Sentry from '@sentry/react'
import { doc } from 'firebase/firestore'
import { useSearchParams } from 'react-router-dom'
import { errorResponse } from '../../lib/errorResponse'
import { ImpersonationBar } from '../../ImpersonationBar'
import { sendVerificationEmail } from '../../lib/sendVerificationEmail'

const { Title, Paragraph, Text } = Typography

function VerifyEmail() {
  const firestore = useFirestore()
  const functions = useFunctions()
  const auth = useAuth()
  const { t } = useTranslation()
  const logoBlack = useLogo('bw')

  const { data: profile } = useFirestoreDocData(
    doc(firestore, 'userprofiles', auth.currentUser.uid),
    { idField: 'uid' }
  )

  const [resendLoading, setResendLoading] = React.useState(false)
  const [cooldown, setCooldown] = React.useState(0)
  const hasAutoVerificationSent = Boolean(
    _get(profile, 'emailVerificationSent')
  )

  useEffect(() => {
    if (!hasAutoVerificationSent) return
    setCooldown((prev) => (prev > 0 ? prev : 30))
  }, [hasAutoVerificationSent])

  const [searchParams] = useSearchParams()

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const resendVerificationEmail = async () => {
    try {
      setResendLoading(true)

      const currentUser = auth.currentUser
      if (currentUser.emailVerified) {
        message.info(t('frontend:auth.emailAlreadyVerified'))
        return
      }

      await sendVerificationEmail(
        firestore,
        functions,
        currentUser,
        searchParams
      )
      message.success(t('frontend:auth.emailSent'))
      setCooldown(30)
    } catch (error) {
      const isRateLimited = error?.code === 'functions/resource-exhausted'
      if (isRateLimited) {
        message.error(t('frontend:auth.tooManyAttempts'))
        setCooldown(60)
      } else {
        errorResponse(error, t('frontend:auth.verificationEmailError'))
      }
    } finally {
      setResendLoading(false)
    }
  }

  useEffect(() => {
    const reloadInterval = setInterval(async () => {
      await reload(auth.currentUser)
    }, 5000)

    return () => {
      clearInterval(reloadInterval)
    }
  }, [auth.currentUser])

  const signOutOfApp = async () => {
    Sentry.setUser(null)
    await signOut(auth)
    window.location.reload()
  }

  // createUser has not been completed yet
  const loading = !_get(profile, 'createdAt')

  return (
    <Layout className={styles.baseLayout}>
      <ImpersonationBar />
      <Layout style={{ marginTop: '50px' }}>
        <Row gutter={gutter} type="flex" justify="center" align="middle">
          <Col align="center" xs={24} style={{ maxWidth: '450px' }}>
            <img src={logoBlack} alt="cleverlohn" className={styles.logo} />
            <Card
              loading={loading}
              bordered={false}
              style={{ marginTop: '10px' }}
            >
              <Title level={3}>{t('frontend:auth.weSentEmail')}</Title>
              <Paragraph>{t('frontend:auth.verifyEmailIntro')}</Paragraph>
              <Paragraph>
                <Text strong style={{ fontSize: '16px' }}>
                  {auth.currentUser.email}
                </Text>
              </Paragraph>
              <Paragraph>{t('frontend:auth.verifyEmailCheck')}</Paragraph>
              <Text type="secondary">
                <Trans
                  i18nKey="frontend:auth.verifyEmailSpamHint"
                  components={{ strong: <strong /> }}
                />
                <br />
                <br />
                {t('frontend:auth.verifyEmailResendHint')}
              </Text>
              <Button
                block
                style={{ marginTop: '20px' }}
                loading={resendLoading}
                disabled={cooldown > 0}
                onClick={resendVerificationEmail}
              >
                {cooldown > 0
                  ? t('frontend:auth.resendWithCooldown', { seconds: cooldown })
                  : t('frontend:auth.resendVerificationEmail')}
              </Button>
              <Button
                block
                type="link"
                style={{ marginTop: '10px' }}
                onClick={signOutOfApp}
              >
                {t('frontend:auth.signOut')}
              </Button>
            </Card>
          </Col>
        </Row>
      </Layout>
    </Layout>
  )
}

export default VerifyEmail
